import { RESOURCE_NAMES } from "./resources.js";
/**
 * Display name of every building, shared by all races (races differ only by
 * sprite). Every player-facing mention of a building reads from here.
 */
export const BUILDING_NAMES = {
    metalMine: "Poly-Carbon Extractor",
    mineralRefinery: "Crystals Mine",
    gasPlant: "Hydrogen Refinery",
    solarPlant: "Solar Plant",
    fusionPlant: "Fusion Plant",
    hangars: "Launcher",
    shipyard: "Factory",
    laboratory: "Research Center",
    techCenter: "Tech Center",
    government: "Command Center",
    robotFactory: "Engineering Facility",
    automationCenter: "Automation Center",
    metalStorage: "Poly-Carbon Storage",
    mineralStorage: "Crystals Storage",
    gasStorage: "Hydrogen Tank",
    recyclingPlant: "Recovery Center",
    tradeCenter: "Market",
};
/**
 * All building definitions.
 *
 * Costs & multipliers follow `docs/game-details/buildings.md`. Every entry
 * uses the breakpoint form for `costMultipliers` — even flat curves are a
 * single-entry `[{ upToLevel: maxLevel, multiplier }]`.
 *
 * Energy costs are the exception: they follow a fixed curve (×1.25 up to
 * L20, ×1.1 afterwards) applied to `baseCost.energy`, computed in
 * `calculateBuildingUpgradeCost` — `costMultipliers` do not apply to energy.
 * Solar Plant and Fusion Plant have `baseCost.energy = 0` (they generate
 * energy). Upgrading a building adds its cached `nextCost.energy` to
 * `planet.resources.energy.consumed`, which the tick subtracts from income
 * to yield the current available energy.
 */
export const BUILDING_DEFINITIONS = {
    // -----------------------------------------------------------------------
    // Resource zone
    // -----------------------------------------------------------------------
    metalMine: {
        id: "metalMine",
        name: BUILDING_NAMES.metalMine,
        description: "Produces the bedrock structural element of your planet, used for building, synthetic plating and base infrastructure.",
        zone: "resource",
        baseCost: { metal: 24, minerals: 10, energy: 2 },
        costMultipliers: [{ upToLevel: 20, multiplier: 1.4 }, { upToLevel: 50, multiplier: 1.2 }],
        maxLevel: 50,
        requirements: [],
        production: { metal: 20 },
        baseProduction: 20,
        productionMultipliers: [
            { upToLevel: 10, multiplier: 1.3 },
            { upToLevel: 20, multiplier: 1.25 },
            { upToLevel: 50, multiplier: 1.1 },
        ],
    },
    mineralRefinery: {
        id: "mineralRefinery",
        name: BUILDING_NAMES.mineralRefinery,
        description: "Collects high-purity geological resources used for specialized tech, optics and laser systems.",
        zone: "resource",
        baseCost: { metal: 24, minerals: 12, energy: 2 },
        costMultipliers: [{ upToLevel: 20, multiplier: 1.4 }, { upToLevel: 50, multiplier: 1.2 }],
        maxLevel: 50,
        requirements: [],
        production: { minerals: 20 },
        baseProduction: 20,
        productionMultipliers: [
            { upToLevel: 10, multiplier: 1.3 },
            { upToLevel: 20, multiplier: 1.2 },
            { upToLevel: 50, multiplier: 1.1 },
        ],
    },
    gasPlant: {
        id: "gasPlant",
        name: BUILDING_NAMES.gasPlant,
        description: `Purifies ${RESOURCE_NAMES.gas}, the main source of ship fuel.`,
        zone: "resource",
        baseCost: { metal: 112, minerals: 35, energy: 5 },
        costMultipliers: [{ upToLevel: 20, multiplier: 1.4 }, { upToLevel: 50, multiplier: 1.2 }],
        maxLevel: 50,
        requirements: [],
        production: { gas: 15 },
        baseProduction: 15,
        productionMultipliers: [
            { upToLevel: 10, multiplier: 1.3 },
            { upToLevel: 20, multiplier: 1.2 },
            { upToLevel: 50, multiplier: 1.1 },
        ],
    },
    solarPlant: {
        id: "solarPlant",
        name: BUILDING_NAMES.solarPlant,
        description: "Generates energy from the sun",
        zone: "resource",
        baseCost: { metal: 75, minerals: 30, energy: 0 },
        costMultipliers: [{ upToLevel: 20, multiplier: 1.4 }, { upToLevel: 50, multiplier: 1.2 }],
        maxLevel: 50,
        requirements: [],
        production: { energy: 20 },
        baseProduction: 20,
        productionMultipliers: [{ upToLevel: 35, multiplier: 1.2 }, { upToLevel: 50, multiplier: 1.1 }],
    },
    fusionPlant: {
        id: "fusionPlant",
        name: BUILDING_NAMES.fusionPlant,
        description: "Generates energy through nuclear fusion",
        zone: "resource",
        baseCost: { metal: 1500, minerals: 500, gas: 250, energy: 0 },
        costMultipliers: [{ upToLevel: 10, multiplier: 1.5 }, { upToLevel: 20, multiplier: 1.4 }],
        maxLevel: 20,
        requirements: [
            { type: "building", id: "solarPlant", level: 10 },
            { type: "science", id: "physics", level: 5 },
            { type: "science", id: "chemistry", level: 5 },
        ],
        production: { energy: 150 },
        baseProduction: 150,
        productionMultipliers: [{ upToLevel: 10, multiplier: 1.33 }, { upToLevel: 20, multiplier: 1.25 }],
    },
    hangars: {
        id: "hangars",
        name: BUILDING_NAMES.hangars,
        description: "Provides population capacity for ships and defences independently — each unit type gets the full cap (not shared)",
        zone: "resource",
        baseCost: { metal: 200, minerals: 35, energy: 5 },
        costMultipliers: [
            { upToLevel: 10, multiplier: 1.5 },
            { upToLevel: 20, multiplier: 2.0 },
        ],
        maxLevel: 20,
        requirements: [{ type: "building", id: "robotFactory", level: 1 }],
        baseProduction: 70,
        productionMultipliers: [
            { upToLevel: 10, multiplier: 1.4 },
            { upToLevel: 20, multiplier: 1.35 },
        ],
    },
    // -----------------------------------------------------------------------
    // Military zone
    // -----------------------------------------------------------------------
    shipyard: {
        id: "shipyard",
        name: BUILDING_NAMES.shipyard,
        description: "Constructs ships and defense systems",
        zone: "military",
        baseCost: { metal: 500, minerals: 250, gas: 100, energy: 10 },
        costMultipliers: [
            { upToLevel: 10, multiplier: 2.25 },
            { upToLevel: 15, multiplier: 2.0 },
        ],
        maxLevel: 15,
        requirements: [],
    },
    laboratory: {
        id: "laboratory",
        name: BUILDING_NAMES.laboratory,
        description: "Enables research of new technologies",
        zone: "military",
        baseCost: { metal: 170, minerals: 340, gas: 170, energy: 10 },
        costMultipliers: [
            { upToLevel: 10, multiplier: 1.7 },
            { upToLevel: 20, multiplier: 1.85 },
        ],
        maxLevel: 20,
        requirements: [{ type: "building", id: "robotFactory", level: 1 }],
    },
    techCenter: {
        id: "techCenter",
        name: BUILDING_NAMES.techCenter,
        description: "Upgrades ships (Ship Upgrade building)",
        zone: "military",
        baseCost: { metal: 250, minerals: 150, gas: 300, energy: 10 },
        costMultipliers: [
            { upToLevel: 5, multiplier: 2.5 },
            { upToLevel: 10, multiplier: 2.55 },
        ],
        maxLevel: 10,
        requirements: [{ type: "building", id: "shipyard", level: 1 }],
    },
    government: {
        id: "government",
        name: BUILDING_NAMES.government,
        description: "Political building required to manage your alliance and Team planet",
        zone: "military",
        baseCost: { metal: 2000, minerals: 1500, gas: 650, energy: 5 },
        costMultipliers: [{ upToLevel: 10, multiplier: 1.9 }],
        maxLevel: 10,
        requirements: [],
    },
    // -----------------------------------------------------------------------
    // Industry zone
    // -----------------------------------------------------------------------
    robotFactory: {
        id: "robotFactory",
        name: BUILDING_NAMES.robotFactory,
        description: "Produces robots that boost resource production and speeds up building construction",
        zone: "industry",
        baseCost: { metal: 400, minerals: 120, gas: 200, energy: 3 },
        costMultipliers: [
            { upToLevel: 5, multiplier: 1.5 },
            { upToLevel: 10, multiplier: 1.6 },
        ],
        maxLevel: 20,
        requirements: [],
    },
    automationCenter: {
        id: "automationCenter",
        name: BUILDING_NAMES.automationCenter,
        description: "Reduces ship & defence construction time and grants additional production robots",
        zone: "industry",
        baseCost: { metal: 1000000, minerals: 500000, gas: 100000, energy: 200 },
        costMultipliers: [{ upToLevel: 5, multiplier: 1.5 }],
        maxLevel: 5,
        requirements: [{ type: "building", id: "robotFactory", level: 10 }],
    },
    metalStorage: {
        id: "metalStorage",
        name: BUILDING_NAMES.metalStorage,
        description: `Increases ${RESOURCE_NAMES.metal} storage capacity`,
        zone: "industry",
        baseCost: { metal: 2000, energy: 1 },
        costMultipliers: [{ upToLevel: 10, multiplier: 1.5 }, { upToLevel: 20, multiplier: 1.4 }, { upToLevel: 30, multiplier: 1.3 }],
        maxLevel: 30,
        requirements: [{ type: "building", id: "metalMine", level: 1 }],
    },
    mineralStorage: {
        id: "mineralStorage",
        name: BUILDING_NAMES.mineralStorage,
        description: `Increases ${RESOURCE_NAMES.minerals} storage capacity`,
        zone: "industry",
        baseCost: { metal: 1500, minerals: 500, energy: 1 },
        costMultipliers: [{ upToLevel: 10, multiplier: 1.5 }, { upToLevel: 20, multiplier: 1.4 }, { upToLevel: 30, multiplier: 1.3 }],
        maxLevel: 30,
        requirements: [{ type: "building", id: "mineralRefinery", level: 1 }],
    },
    gasStorage: {
        id: "gasStorage",
        name: BUILDING_NAMES.gasStorage,
        description: `Increases ${RESOURCE_NAMES.gas} storage capacity`,
        zone: "industry",
        baseCost: { metal: 2000, energy: 1 },
        costMultipliers: [{ upToLevel: 10, multiplier: 1.5 }, { upToLevel: 20, multiplier: 1.3 }, { upToLevel: 30, multiplier: 1.25 }],
        maxLevel: 30,
        requirements: [{ type: "building", id: "gasPlant", level: 1 }],
    },
    recyclingPlant: {
        id: "recyclingPlant",
        name: BUILDING_NAMES.recyclingPlant,
        description: `Converts ${RESOURCE_NAMES.scrap} into ships in parallel to the ${BUILDING_NAMES.shipyard}`,
        zone: "industry",
        baseCost: { metal: 22500, minerals: 14500, gas: 5000, energy: 10 },
        // Conversion rate: RECYCLING_INCOME_BASE_PERCENT at L1, linearly up to
        // RECYCLING_INCOME_MAX_PERCENT at maxLevel (issue #21). Each level
        // also adds one parallel recycle queue slot.
        costMultipliers: [
            { upToLevel: 5, multiplier: 1.5 },
            { upToLevel: 10, multiplier: 1.75 },
        ],
        maxLevel: 10,
        requirements: [
            { type: "building", id: "shipyard", level: 5 },
            { type: "science", id: "chemistry", level: 6 },
        ],
    },
    tradeCenter: {
        id: "tradeCenter",
        name: BUILDING_NAMES.tradeCenter,
        description: "Enables resource trading (1 trade per 6 hours per level)",
        zone: "industry",
        baseCost: { metal: 5000, minerals: 3500, gas: 100, energy: 5 },
        costMultipliers: [
            { upToLevel: 5, multiplier: 1.5 },
            { upToLevel: 10, multiplier: 1.75 },
        ],
        maxLevel: 10,
        requirements: [],
    },
};
// ---------------------------------------------------------------------------
// Recovery Center economics (issue #21) — scrap → ships, not the
// baseCost/costMultipliers above (those price the BUILDING's own upgrades).
// ---------------------------------------------------------------------------
/** Recovery Center conversion rate at level 1. */
export const RECYCLING_INCOME_BASE_PERCENT = 50;
/** Recovery Center conversion rate at its max level (`BUILDING_DEFINITIONS.recyclingPlant.maxLevel`). */
export const RECYCLING_INCOME_MAX_PERCENT = 100;
/**
 * Cancelling a queued recycle batch refunds a uniformly-rolled fraction of
 * its scrap cost in this range ("Cancellation returns 75-80% (random) of
 * used up scrap" — issue #21). The roll itself is injected by the caller —
 * the game engine stays deterministic.
 */
export const SCRAP_RECYCLE_REFUND_MIN = 0.75;
export const SCRAP_RECYCLE_REFUND_MAX = 0.80;
/**
 * Anti-exploit cap on recycle batches queued per rolling 24h window per
 * planet ("A user might not queue more than 70 recycle items per day" —
 * issue #21), independent of the parallel-slot limit (one slot per
 * Recovery Center level).
 */
export const SCRAP_RECYCLE_DAILY_LIMIT = 70;
//# sourceMappingURL=buildings.js.map